import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { collection, query, where, onSnapshot, Timestamp, doc, getDoc } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import { reconnectFirestore } from '../firebase/reconnect';
import { getTotalCounts } from '../firebase/utils';
import { useAuth } from '../firebase/useAuth';
import { useMetadata } from './MetadataProvider';
import moment from 'moment-timezone';

const ForecastContext = createContext();

export const useForecast = () => {
  return useContext(ForecastContext);
};

export const ForecastProvider = ({ children }) => {
  const { user, userData, subscriptionStatus } = useAuth();
  const { reviewedWordsData, reviewedSentencesData } = useMetadata();

  // Forecast-specific state
  const [reviewForecasts, setReviewForecasts] = useState({ sentences: {}, words: {} });
  const [sentenceCounts, setSentenceCounts] = useState({});
  const [sentenceCountsByWk, setSentenceCountsByWk] = useState({});
  const [wordCounts, setWordCounts] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [cramWordsCount, setCramWordsCount] = useState(0);
  const [cramSentencesCount, setCramSentencesCount] = useState(0);
  const [cramCountsLoading, setCramCountsLoading] = useState(true);
  const [recentUnlocksWordsCount, setRecentUnlocksWordsCount] = useState(0);
  const [recentUnlocksSentencesCount, setRecentUnlocksSentencesCount] = useState(0);
  const [recentUnlocksCountsLoading, setRecentUnlocksCountsLoading] = useState(true);

  // Loading states
  const [countsLoading, setCountsLoading] = useState(true);
  const [forecastLoading, setForecastLoading] = useState(true);
  const [alertsLoading, setAlertsLoading] = useState(true);

  // Error state
  const [error, setError] = useState(null);

  // Drives the 7-day query window. Updated only on date change (in triggerRefresh),
  // never inside onSnapshot — avoids a listener rebuild loop.
  const [queryBaseDate, setQueryBaseDate] = useState(() => moment().format('YYYY-MM-DD'));

  // Refs to store latest Firestore snapshots for client-side recompute
  const wordSnapshotRef = useRef(null);
  const sentenceSnapshotRef = useRef(null);
  // Tracks the date+hour when forecast was last computed
  const lastComputedRef = useRef({ date: null, hour: null });

  // Refs for reviewed data — avoids recreating listeners on every review completion
  const reviewedWordsDataRef = useRef(reviewedWordsData);
  const reviewedSentencesDataRef = useRef(reviewedSentencesData);
  useEffect(() => { reviewedWordsDataRef.current = reviewedWordsData; }, [reviewedWordsData]);
  useEffect(() => { reviewedSentencesDataRef.current = reviewedSentencesData; }, [reviewedSentencesData]);

  // Memoize stable values from userData to prevent unnecessary callback recreation
  const userSettings = useMemo(() => {
    if (!userData?.settings) return null;
    return {
      timezone: userData.settings.timezone,
      review_burned_items: userData.settings.review_burned_items,
      review_frequency: userData.settings.review_frequency,
      words_reviews_per_day: userData.settings.words_reviews_per_day,
      sentence_reviews_per_day: userData.settings.sentence_reviews_per_day,
    };
  }, [userData?.settings]);

  // Memoize subscription status
  const isSubscribed = useMemo(() => subscriptionStatus?.isSubscribed || false, [subscriptionStatus?.isSubscribed]);

  // Core forecast computation — runs on existing snapshot data with current time (0 Firestore reads)
  const processSnapshotData = useCallback(
    (snapshot, contentType) => {
      if (!userSettings) return;

      const forecast = {};
      const userTimezone = userSettings.timezone;
      const isHourlyEnabled = userSettings.review_frequency === 'hour';

      const dailyLimit = contentType === 'words'
        ? userSettings.words_reviews_per_day || -1
        : userSettings.sentence_reviews_per_day || -1;

      const reviewedData = contentType === 'words' ? reviewedWordsDataRef.current : reviewedSentencesDataRef.current;

      // Collect all items first, then organize them
      const allItems = [];
      snapshot.docs.forEach((doc) => {
        const item = doc.data();

        // Check level permissions
        let levelAllowed = false;
        if (contentType === 'sentences') {
          levelAllowed = isSubscribed ? item.level >= 0 : item.level === 5;
        } else {
          levelAllowed = isSubscribed ? item.level >= 0 : [0, 5].includes(item.level);
        }

        if (levelAllowed && item.next_review) {
          const reviewDate = item.next_review.toDate();
          const reviewMoment = moment(reviewDate).tz(userTimezone);
          allItems.push({ reviewMoment, item });
        }
      });

      // Sort items by review time (earliest first)
      allItems.sort((a, b) => a.reviewMoment.valueOf() - b.reviewMoment.valueOf());

      const todayMoment = moment().tz(userTimezone);
      const currentHour = todayMoment.hour();

      // Process items for daily forecast
      allItems.forEach(({ reviewMoment }) => {
        const dayKey = reviewMoment.isBefore(todayMoment, 'day') ? todayMoment.format('YYYY-MM-DD') : reviewMoment.format('YYYY-MM-DD');
        forecast[dayKey] = (forecast[dayKey] || 0) + 1;
      });

      // Process items for hourly forecast (cumulative approach)
      if (isHourlyEnabled) {
        const todayKey = todayMoment.format('YYYY-MM-DD');

        // Create a map to store hourly data for each day
        const hourlyDataByDay = {};

        allItems.forEach(({ reviewMoment }) => {
          const itemDay = reviewMoment.format('YYYY-MM-DD');
          const itemHour = reviewMoment.hour();

          // Initialize hourly data for this day if not exists
          if (!hourlyDataByDay[itemDay]) {
            hourlyDataByDay[itemDay] = {};
          }

          if (itemDay === todayKey) {
            // For today, handle current hour accumulation
            if (reviewMoment.isSameOrBefore(todayMoment.clone().endOf('hour'))) {
              // Items due now or earlier go into current hour
              hourlyDataByDay[itemDay][currentHour] = (hourlyDataByDay[itemDay][currentHour] || 0) + 1;
            } else {
              // Future items go into their scheduled hour
              hourlyDataByDay[itemDay][itemHour] = (hourlyDataByDay[itemDay][itemHour] || 0) + 1;
            }
          } else if (reviewMoment.isBefore(todayMoment, 'day')) {
            // All overdue items (from previous days) go into current hour of today
            if (!hourlyDataByDay[todayKey]) {
              hourlyDataByDay[todayKey] = {};
            }
            hourlyDataByDay[todayKey][currentHour] = (hourlyDataByDay[todayKey][currentHour] || 0) + 1;
          } else {
            // Future days - items go into their scheduled hour
            hourlyDataByDay[itemDay][itemHour] = (hourlyDataByDay[itemDay][itemHour] || 0) + 1;
          }
        });

        // Add hourly data to forecast for each day
        Object.entries(hourlyDataByDay).forEach(([dayKey, hourlyData]) => {
          forecast[`${dayKey}:hourly`] = hourlyData;
        });
      }

      // Apply daily limits to forecast (special handling for hourly)
      if (dailyLimit !== -1 && reviewedData) {
        // Process all days that have forecasts
        const sortedDayKeys = Object.keys(forecast)
          .filter(key => !key.includes(':hourly'))
          .sort();

        let carryoverReviews = 0;
        const carryoverHourlyData = {}; // Store hourly data that needs to be carried over

        for (const dayKey of sortedDayKeys) {
          if (forecast[dayKey] !== undefined) {
            const reviewedForDay = reviewedData?.[dayKey] || 0;
            const availableCapacity = Math.max(0, dailyLimit - reviewedForDay);
            const originalDailyTotal = forecast[dayKey];
            const totalWithCarryover = originalDailyTotal + carryoverReviews;

            if (totalWithCarryover > availableCapacity) {
              // We have more reviews than capacity
              forecast[dayKey] = availableCapacity;
              carryoverReviews = totalWithCarryover - availableCapacity;

              // For hourly data, apply limits and track what needs to be carried over
              const hourlyKey = `${dayKey}:hourly`;
              if (forecast[hourlyKey] && isHourlyEnabled) {
                const originalHourlyData = forecast[hourlyKey];
                const newHourlyData = {};
                let remainingCapacity = availableCapacity;

                // First, process any carryover hourly data from previous day
                Object.entries(carryoverHourlyData).forEach(([, count]) => {
                  const canTake = Math.min(count, remainingCapacity);
                  if (canTake > 0) {
                    newHourlyData[0] = (newHourlyData[0] || 0) + canTake; // Add to first hour of day
                    remainingCapacity -= canTake;
                  }
                });

                // Clear carryover since we've processed it
                Object.keys(carryoverHourlyData).forEach(key => delete carryoverHourlyData[key]);

                // Sort hours
                const allHours = Object.keys(originalHourlyData).map(h => parseInt(h)).sort((a, b) => a - b);

                // For today, start from current hour; for future days, start from beginning.
                // indexOf returns -1 when no items landed in currentHour (all items are in
                // future hours) — clamp to 0 so the loop still processes those future hours.
                const isToday = dayKey === todayMoment.format('YYYY-MM-DD');
                const startIndex = isToday ? Math.max(0, allHours.indexOf(currentHour)) : 0;

                // Process hours chronologically
                for (let i = startIndex; i < allHours.length; i++) {
                  const hour = allHours[i];
                  const hourCount = originalHourlyData[hour];
                  const canTake = Math.min(hourCount, remainingCapacity);

                  if (canTake > 0) {
                    newHourlyData[hour] = canTake;
                    remainingCapacity -= canTake;
                  }

                  // Track what couldn't be taken for carryover
                  if (hourCount > canTake) {
                    carryoverHourlyData[hour] = (carryoverHourlyData[hour] || 0) + (hourCount - canTake);
                  }
                }

                forecast[hourlyKey] = newHourlyData;
              }
            } else {
              // We have capacity for all reviews including carryover
              forecast[dayKey] = totalWithCarryover;

              // Handle hourly data with carryover
              const hourlyKey = `${dayKey}:hourly`;
              if (forecast[hourlyKey] && isHourlyEnabled) {
                const originalHourlyData = forecast[hourlyKey];
                const newHourlyData = {};

                // Add carryover hourly data to first hour
                if (Object.keys(carryoverHourlyData).length > 0) {
                  let carryoverTotal = 0;
                  Object.values(carryoverHourlyData).forEach(count => { carryoverTotal += count; });
                  newHourlyData[0] = (originalHourlyData[0] || 0) + carryoverTotal;

                  // Clear carryover since we've processed it
                  Object.keys(carryoverHourlyData).forEach(key => delete carryoverHourlyData[key]);
                }

                // Merge remaining hourly data (additive to preserve any carryover already in slot 0)
                Object.entries(originalHourlyData).forEach(([hour, count]) => {
                  newHourlyData[hour] = (newHourlyData[hour] || 0) + count;
                });

                forecast[hourlyKey] = newHourlyData;
              }

              carryoverReviews = 0;
            }
          }
        }
      }

      // Update the appropriate forecast state
      setReviewForecasts((prev) => ({
        ...prev,
        [contentType]: forecast,
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userSettings, isSubscribed]
  );

  // Ref kept current so onSnapshot closure always calls the latest processSnapshotData
  // without needing it in setupReviewForecastListener's deps (which would tear down listeners
  // on every isSubscribed change even though the query itself is unaffected)
  const processSnapshotDataRef = useRef(processSnapshotData);
  useEffect(() => { processSnapshotDataRef.current = processSnapshotData; }, [processSnapshotData]);

  // Re-run forecast computation on stored snapshots with current time (0 Firestore reads)
  const recomputeForecasts = useCallback(() => {
    if (wordSnapshotRef.current) processSnapshotData(wordSnapshotRef.current, 'words');
    if (sentenceSnapshotRef.current) processSnapshotData(sentenceSnapshotRef.current, 'sentences');
  }, [processSnapshotData]);

  // Recompute forecasts whenever subscription status changes.
  // This handles both the initial load race condition (delayed subscription data)
  // and real-time subscription upgrades/expirations while using the dashboard.
  useEffect(() => {
    recomputeForecasts();
  }, [isSubscribed, recomputeForecasts]);

  // Called from visibilitychange and logo click
  const triggerRefresh = useCallback(async () => {
    if (!userSettings) return; // not logged in or settings not yet loaded

    await reconnectFirestore();

    const now = moment().tz(userSettings?.timezone || 'UTC');
    const { date: lastDate, hour: lastHour } = lastComputedRef.current;
    const currentDate = now.format('YYYY-MM-DD');
    const currentHour = now.hour();
    const isHourlyEnabled = userSettings?.review_frequency === 'hour';

    const dateChanged = currentDate !== lastDate;
    const hourChanged = isHourlyEnabled && currentHour !== lastHour;

    if (dateChanged) {
      // Day changed — rebuild Firestore listeners with a fresh 7-day window.
      // The new onSnapshot will update lastComputedRef and recompute automatically.
      lastComputedRef.current = { date: currentDate, hour: currentHour };
      setQueryBaseDate(currentDate);
    } else if (hourChanged) {
      // Same day, new hour — recompute locally from cached snapshots (0 Firestore reads).
      const hasSnapshots = wordSnapshotRef.current !== null || sentenceSnapshotRef.current !== null;
      if (hasSnapshots) {
        lastComputedRef.current = { date: currentDate, hour: currentHour };
        recomputeForecasts();
      }
    }
  }, [recomputeForecasts, userSettings]);

  // Listen for app returning to foreground
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        triggerRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [triggerRefresh]);

  // Helper function to set up real-time review forecast listeners
  const setupReviewForecastListener = useCallback(
    (contentType) => {
      if (!user?.uid || !userSettings) return () => { };

      const collectionName = contentType === 'words' ? 'user_words' : 'user_sentences';
      const collectionRef = collection(firestore, 'users', user.uid, collectionName);

      const userTimezone = userSettings.timezone;
      const weekEnd = Timestamp.fromDate(moment().tz(userTimezone).add(6, 'days').endOf('day').utc().toDate());

      const reviewQuery = query(
        collectionRef,
        where('next_review', '<=', weekEnd),
        where('skipped', '==', false),
        where('srs_stage', '<', userSettings.review_burned_items ? 8 : 7)
      );

      const unsubscribe = onSnapshot(
        reviewQuery,
        (snapshot) => {
          // Store snapshot for future client-side recomputes
          if (contentType === 'words') wordSnapshotRef.current = snapshot;
          else sentenceSnapshotRef.current = snapshot;

          // Track when this was last computed (use user timezone to match triggerRefresh check)
          const now = moment().tz(userSettings.timezone);
          lastComputedRef.current = { date: now.format('YYYY-MM-DD'), hour: now.hour() };

          processSnapshotDataRef.current(snapshot, contentType);
        },
        (err) => {
          console.error(`Error in ${contentType} forecast listener:`, err);
          setError(err);
        }
      );

      return unsubscribe;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.uid, userSettings, queryBaseDate]
  );

  useEffect(() => {
    if (!user?.uid || !userSettings) {
      setReviewForecasts({ sentences: {}, words: {} });
      setForecastLoading(false);
      return;
    }

    // Set up real-time listeners for review forecasts
    const cleanupSentenceForecast = setupReviewForecastListener('sentences');
    const cleanupWordForecast = setupReviewForecastListener('words');

    // Set loading to false once listeners are established
    setForecastLoading(false);

    return () => {
      cleanupSentenceForecast();
      cleanupWordForecast();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, userSettings, queryBaseDate]); // setupReviewForecastListener intentionally omitted — its deps are a superset of these

  // Fetch alerts from dashboard collection
  const fetchAlerts = async () => {
    try {
      const alertsDoc = await getDoc(doc(firestore, 'dashboard', 'alerts'));
      if (alertsDoc.exists()) {
        const data = alertsDoc.data();
        const alertsArray = data.alerts || [];
        setAlerts(alertsArray);
        setAlertsLoading(false);
        return alertsArray;
      } else {
        setAlerts([]);
        setAlertsLoading(false);
        return [];
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
      setAlerts([]);
      setAlertsLoading(false);
      return [];
    }
  };

  // Fetch global counts once on mount — not user-specific, no reason to re-fetch on auth change
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [sentenceByWkData, wordCountsData] = await Promise.all([
          getTotalCounts('sentences'),
          getTotalCounts('words'),
        ]);
        // Derive per-JLPT totals from the wk histogram so we have one source of truth.
        const sentenceTotals = {};
        for (const [jlpt, buckets] of Object.entries(sentenceByWkData || {})) {
          sentenceTotals[jlpt] = Object.values(buckets || {}).reduce((sum, n) => sum + (n || 0), 0);
        }
        setSentenceCountsByWk(sentenceByWkData);
        setSentenceCounts(sentenceTotals);
        setWordCounts(wordCountsData);
        setCountsLoading(false);
      } catch (error) {
        console.error('Error fetching counts:', error);
        setCountsLoading(false);
      }
    };
    fetchCounts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch alerts once when the user becomes available
  useEffect(() => {
    if (!user?.uid) {
      setAlertsLoading(false);
      return;
    }
    fetchAlerts();
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time cram counts — items with last_mistake_at within the past 24h
  useEffect(() => {
    if (!user?.uid) {
      setCramWordsCount(0);
      setCramSentencesCount(0);
      setCramCountsLoading(false);
      return;
    }
    setCramCountsLoading(true);
    let wordsFired = false;
    let sentencesFired = false;
    const cutoff = Timestamp.fromDate(new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)); // 3-day cram window
    const unsubWords = onSnapshot(
      query(collection(firestore, 'users', user.uid, 'user_words'), where('last_mistake_at', '>=', cutoff)),
      (snap) => {
        setCramWordsCount(snap.size);
        wordsFired = true;
        if (sentencesFired) setCramCountsLoading(false);
      },
      (err) => { console.error('Error in cram words listener:', err); setCramCountsLoading(false); }
    );
    const unsubSentences = onSnapshot(
      query(collection(firestore, 'users', user.uid, 'user_sentences'), where('last_mistake_at', '>=', cutoff)),
      (snap) => {
        setCramSentencesCount(snap.size);
        sentencesFired = true;
        if (wordsFired) setCramCountsLoading(false);
      },
      (err) => { console.error('Error in cram sentences listener:', err); setCramCountsLoading(false); }
    );
    return () => { unsubWords(); unsubSentences(); };
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time recent unlocks counts — items with learned_at within the past 7 days
  useEffect(() => {
    if (!user?.uid) {
      setRecentUnlocksWordsCount(0);
      setRecentUnlocksSentencesCount(0);
      setRecentUnlocksCountsLoading(false);
      return;
    }
    setRecentUnlocksCountsLoading(true);
    let wordsFired = false;
    let sentencesFired = false;
    const cutoff = Timestamp.fromDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)); // 7-day recent unlocks window
    const unsubWords = onSnapshot(
      query(collection(firestore, 'users', user.uid, 'user_words'), where('learned_at', '>=', cutoff), where('skipped', '==', false)),
      (snap) => {
        setRecentUnlocksWordsCount(snap.size);
        wordsFired = true;
        if (sentencesFired) setRecentUnlocksCountsLoading(false);
      },
      (err) => { console.error('Error in recent unlocks words listener:', err); setRecentUnlocksCountsLoading(false); }
    );
    const unsubSentences = onSnapshot(
      query(collection(firestore, 'users', user.uid, 'user_sentences'), where('learned_at', '>=', cutoff), where('skipped', '==', false)),
      (snap) => {
        setRecentUnlocksSentencesCount(snap.size);
        sentencesFired = true;
        if (wordsFired) setRecentUnlocksCountsLoading(false);
      },
      (err) => { console.error('Error in recent unlocks sentences listener:', err); setRecentUnlocksCountsLoading(false); }
    );
    return () => { unsubWords(); unsubSentences(); };
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    // Forecast data
    reviewForecasts,
    sentenceCounts,
    sentenceCountsByWk,
    wordCounts,
    alerts,

    // Loading states
    countsLoading,
    forecastLoading,
    alertsLoading,
    loading: countsLoading || forecastLoading,

    // Cram counts
    cramWordsCount,
    cramSentencesCount,
    cramCountsLoading,

    // Recent unlocks counts
    recentUnlocksWordsCount,
    recentUnlocksSentencesCount,
    recentUnlocksCountsLoading,

    // Error state
    error,

    // Manual refresh trigger (reconnect + date/hour recompute)
    triggerRefresh,
  };

  return <ForecastContext.Provider value={value}>{children}</ForecastContext.Provider>;
};